import { useState } from "react";
import { Affix, Button, Empty, EnvBadge, ErrorNotice, Field, InfoTip, Input, Loading, Modal, Panel, Segmented, Select, useToast } from "../../components/ui";
import { gameApi } from "../../lib/api";
import { formatMoney, parseMoney, type CurrencyLike } from "../../lib/money";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { Link } from "../../lib/router";
import { useClient } from "../../lib/session";
import { formatGameTime, isoToZonedInput, untilText, zonedInputToIso } from "../../lib/time";
import type { Bank, FeeEvent, FeeKind, FeeModel, FeeQuote, FeeRule, FeeRuleInput } from "../../lib/types";
import type { GameTabProps } from "./Game";

const EVENTS: { value: FeeEvent; label: string; help: string }[] = [
  { value: "transfer", label: "Player-to-player transfer", help: "Charged to the sender on top of the amount." },
  { value: "hold_capture", label: "Hold capture (purchase)", help: "Reserved when the hold is placed, charged when it is captured." },
  { value: "account_maintenance", label: "Monthly account maintenance", help: "Charged once per game month; accounts that can't pay are skipped." },
  { value: "card_issuance", label: "Card issuance", help: "A fixed fee charged to the cardholder when a debit card is issued." },
  { value: "card_payment", label: "Card payment", help: "Reserved when a card payment is authorised, charged to the cardholder on capture." },
  { value: "savings_excess_withdrawal", label: "Extra savings withdrawal", help: "Charged on a savings withdrawal beyond the product's monthly allowance, when its action is \"fee\"." },
];
const eventLabel = (e: FeeEvent) => EVENTS.find((x) => x.value === e)?.label ?? e;

/** Basis points as a percentage string: 150 -> "1.5". */
const pct = (bps: number) => (bps / 100).toString();

/** "₦50.00 + 1% (min ₦10.00, max ₦500.00)" */
export function describeCharge(r: Pick<FeeRule, "model" | "fixed_minor" | "percentage_bps" | "min_fee_minor" | "max_fee_minor">, cur: CurrencyLike): string {
  const parts = [];
  if (r.model !== "percentage") parts.push(formatMoney(r.fixed_minor, cur));
  if (r.model !== "fixed") parts.push(`${pct(r.percentage_bps)}%`);
  const caps = [];
  if (r.min_fee_minor != null) caps.push(`min ${formatMoney(r.min_fee_minor, cur)}`);
  if (r.max_fee_minor != null) caps.push(`max ${formatMoney(r.max_fee_minor, cur)}`);
  return parts.join(" + ") + (caps.length ? ` (${caps.join(", ")})` : "");
}

/** "₦1,000.00 or more", "up to ₦5,000.00", "₦1,000.00 to ₦5,000.00" or "Always". */
export function describeConditions(r: Pick<FeeRule, "event" | "min_amount_minor" | "max_amount_minor">, cur: CurrencyLike): string {
  const subject = r.event === "account_maintenance" ? "Balance " : "";
  const { min_amount_minor: lo, max_amount_minor: hi } = r;
  if (lo == null && hi == null) return "Always";
  if (lo != null && hi != null) return `${subject}${formatMoney(lo, cur)} to ${formatMoney(hi, cur)}`;
  if (lo != null) return `${subject}${formatMoney(lo, cur)} or more`;
  return `${subject}up to ${formatMoney(hi!, cur)}`;
}

/** The engine's warnings name API fields; say them the way the form does. */
export const humanWarning = (w: string) =>
  w.replace(/^min_fee_minor/, "The minimum fee").replace(/^max_fee_minor/, "The maximum fee").replace(/max_fee_minor/g, "the maximum fee");

const STATES: Record<FeeRule["state"], { label: string; tone: string }> = {
  in_effect: { label: "In effect", tone: "ok" },
  scheduled: { label: "Scheduled", tone: "" },
  ended: { label: "Ended", tone: "bad" },
  inactive: { label: "Paused", tone: "bad" },
};

/** Whether a fee is charging now, and if not, why: "Scheduled · starts in 58 min". */
function FeeState({ rule, gameTime }: { rule: FeeRule; gameTime?: string }) {
  const s = STATES[rule.state] ?? { label: rule.state, tone: "" };
  return (
    <>
      <span className={`badge ${s.tone}`}>{s.label}</span>
      {rule.state === "scheduled" && gameTime && <div className="cell-sub">starts {untilText(rule.effective_from, gameTime)}</div>}
      {rule.state === "ended" && <div className="cell-sub">no longer charged</div>}
    </>
  );
}

const toCode = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^(\d)/, "fee_$1").slice(0, 63);

export function GameFees({ game, env }: GameTabProps) {
  const api = gameApi(useClient(), { gameId: game.id, env });
  const banks = useQuery(`dev:banks:${game.id}:${env}`, () => api.banks());
  const [selected, setSelected] = useState<string | null>(null);
  const list = banks.data?.data ?? [];
  const bank = list.find((b) => b.id === selected) ?? list[0];

  if (banks.loading && !banks.data) return <Loading />;
  if (banks.error) return <ErrorNotice error={banks.error} onRetry={banks.reload} />;
  if (!bank) {
    return (
      <Empty title={`No ${env} banks yet`} action={<Link className="btn primary" to={`/dev/games/${game.id}/banks${env === "production" ? "?env=production" : ""}`}>Create a bank</Link>}>
        Fees and charges belong to a bank. Create one first.
      </Empty>
    );
  }
  return (
    <div className="stack">
      {list.length > 1 && (
        <Field label="Bank">
          <Select value={bank.id} onChange={(e) => setSelected(e.target.value)} style={{ maxWidth: 360 }}>
            {list.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.bank_code})</option>)}
          </Select>
        </Field>
      )}
      <FeeList key={bank.id} gameId={game.id} env={env} bank={bank} />
      <div className="grid-2">
        <QuotePanel gameId={game.id} env={env} bank={bank} />
        <BooksPanel gameId={game.id} env={env} bank={bank} />
      </div>
    </div>
  );
}

function FeeList({ gameId, env, bank }: { gameId: string; env: GameTabProps["env"]; bank: Bank }) {
  const api = gameApi(useClient(), { gameId, env });
  const toast = useToast();
  const key = `dev:fees:${gameId}:${env}:${bank.id}`;
  const fees = useQuery(key, () => api.fees(bank.id));
  const clock = useQuery(`dev:clock:${gameId}:${env}`, () => api.clock());
  const tz = clock.data?.time_zone ?? "UTC";
  const [editing, setEditing] = useState<FeeRule | "new" | null>(null);
  const toggle = useAction(async (r: FeeRule) => api.updateFee(bank.id, r.id, { status: r.status === "active" ? "inactive" : "active" }));
  const cur = bank.base_currency;
  const rules = fees.data?.data ?? [];

  return (
    <Panel
      title={<>Fees & charges for {bank.name} <EnvBadge env={env} /></>}
      sub="Each matching fee is its own line on the sender's statement and in the ledger. Fees go to the bank's fee income; levies to levies payable."
      actions={<Button variant="primary" size="small" onClick={() => setEditing("new")}>Add a fee</Button>}
      flush
    >
      <ErrorNotice error={fees.error ?? toggle.error} onRetry={fees.reload} />
      {fees.loading && !fees.data && <Loading />}
      {fees.data && rules.length === 0 && (
        <Empty title="No fees yet" action={<Button variant="primary" onClick={() => setEditing("new")}>Add a fee</Button>}>
          Transfers, purchases and accounts in this bank are free until you add one.
        </Empty>
      )}
      {rules.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr><th>Fee</th><th>Charged on</th><th>Charge</th><th>Applies to</th><th>Paid to</th><th>Effective (game time, {tz})</th><th>State</th><th /></tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="cell-title">{r.name}</div>
                    <div className="cell-sub mono">{r.code} · v{r.version}</div>
                    {r.warnings.map((w) => <div key={w} className="cell-sub" style={{ color: "var(--warn, #b45309)" }}>⚠ {humanWarning(w)}</div>)}
                  </td>
                  <td>{eventLabel(r.event)}</td>
                  <td className="mono">{describeCharge(r, cur)}</td>
                  <td>{describeConditions(r, cur)}</td>
                  <td>{r.kind === "levy" ? "Levies payable" : "Bank fee income"}<div className="cell-sub">Paid by the sender</div></td>
                  <td>
                    {formatGameTime(r.effective_from, tz)}
                    <div className="cell-sub">{r.effective_to ? `until ${formatGameTime(r.effective_to, tz)}` : "no end"}</div>
                  </td>
                  <td><FeeState rule={r} gameTime={clock.data?.game_time} /></td>
                  <td className="num">
                    <div className="row end" style={{ gap: 6, flexWrap: "nowrap" }}>
                      <Button size="small" onClick={() => setEditing(r)}>Edit</Button>
                      <Button size="small" busy={toggle.pending} onClick={async () => {
                        if (await toggle.run(r)) {
                          invalidate(key);
                          toast(r.status === "active" ? `Paused ${r.name}` : `Resumed ${r.name}`);
                        }
                      }}>{r.status === "active" ? "Pause" : "Resume"}</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <FeeEditor gameId={gameId} env={env} bank={bank} rule={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(r, created) => {
            setEditing(null);
            invalidate(key);
            invalidate(`dev:banks:${gameId}`);
            toast(created ? `Added ${r.name}` : `Saved ${r.name} (version ${r.version})`);
          }} />
      )}
    </Panel>
  );
}

interface FormState {
  name: string; code: string; codeTouched: boolean; event: FeeEvent; kind: FeeKind; model: FeeModel;
  fixed: string; percent: string; minFee: string; maxFee: string; minAmount: string; maxAmount: string;
  status: "active" | "inactive";
  /** Start immediately, or at a game time. Times are kept as RFC 3339 and shown in the game clock's zone. */
  startMode: "now" | "at"; fromIso: string;
  endMode: "never" | "at"; toIso: string;
}

function initial(rule: FeeRule | null, cur: CurrencyLike): FormState {
  const money = (v: number | null | undefined) => (v == null ? "" : formatMoney(v, { ...cur, symbol: null }).replace(` ${cur.code}`, "").replace(/,/g, ""));
  if (!rule) {
    return { name: "", code: "", codeTouched: false, event: "transfer", kind: "fee", model: "fixed", fixed: "", percent: "",
      minFee: "", maxFee: "", minAmount: "", maxAmount: "", status: "active", startMode: "now", fromIso: "", endMode: "never", toIso: "" };
  }
  return {
    name: rule.name, code: rule.code, codeTouched: true, event: rule.event, kind: rule.kind, model: rule.model,
    fixed: rule.model === "percentage" ? "" : money(rule.fixed_minor), percent: rule.model === "fixed" ? "" : pct(rule.percentage_bps),
    minFee: money(rule.min_fee_minor), maxFee: money(rule.max_fee_minor), minAmount: money(rule.min_amount_minor), maxAmount: money(rule.max_amount_minor),
    status: rule.status,
    // A fee that has already started shows "Immediately" (keep its start); a scheduled one shows its time.
    startMode: rule.state === "scheduled" ? "at" : "now", fromIso: rule.effective_from,
    endMode: rule.effective_to ? "at" : "never", toIso: rule.effective_to ?? "",
  };
}

function FeeEditor({ gameId, env, bank, rule, onClose, onSaved }: {
  gameId: string; env: GameTabProps["env"]; bank: Bank; rule: FeeRule | null; onClose: () => void; onSaved: (r: FeeRule, created: boolean) => void;
}) {
  const api = gameApi(useClient(), { gameId, env });
  const clock = useQuery(`dev:clock:${gameId}:${env}`, () => api.clock());
  const cur = bank.base_currency;
  const [f, setF] = useState<FormState>(() => initial(rule, cur));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));
  const tz = clock.data?.time_zone ?? "UTC";
  const gameNow = clock.data?.game_time;
  // Switching to "at a game time" starts from the game's current time, so the value is never an unrelated wall clock.
  const pickStart = (m: FormState["startMode"]) => setF((s) => ({ ...s, startMode: m, fromIso: m === "at" && !s.fromIso ? gameNow ?? "" : s.fromIso }));
  const pickEnd = (m: FormState["endMode"]) => setF((s) => ({ ...s, endMode: m, toIso: m === "at" && !s.toIso ? gameNow ?? "" : s.toIso }));

  const save = useAction(async () => {
    setLocalError(null);
    const money = (label: string, v: string): number | undefined | false => {
      if (!v.trim()) return undefined;
      const n = parseMoney(v, cur.exponent);
      if (n === null) { setLocalError(`${label}: enter an amount with at most ${cur.exponent} decimal places`); return false; }
      return n;
    };
    const fixed = f.model === "percentage" ? 0 : money("Fixed fee", f.fixed);
    const bps = f.model === "fixed" ? 0 : (f.percent.trim() ? parseMoney(f.percent, 2) : null);
    const minFee = money("Minimum fee", f.minFee), maxFee = money("Maximum fee", f.maxFee);
    const minAmount = money("Minimum amount", f.minAmount), maxAmount = money("Maximum amount", f.maxAmount);
    if ([fixed, minFee, maxFee, minAmount, maxAmount].includes(false)) return null;
    if (f.model !== "fixed" && bps === null) { setLocalError("Percentage fee: enter a percentage such as 1 or 1.5"); return null; }
    if (f.startMode === "at" && !f.fromIso) { setLocalError("Starts: pick a game time, or choose Immediately"); return null; }
    if (f.endMode === "at" && !f.toIso) { setLocalError("Ends: pick a game time, or choose Never"); return null; }
    const body: FeeRuleInput = {
      name: f.name.trim(), kind: f.kind, model: f.model, fixed_minor: (fixed as number | undefined) ?? 0, percentage_bps: bps ?? 0,
      status: f.status,
    };
    if (f.startMode === "at") body.effective_from = f.fromIso;
    else if (rule?.state === "scheduled") body.start_now = true; // bring a scheduled fee forward to now
    if (f.endMode === "at") body.effective_to = f.toIso;
    const clear: string[] = [];
    for (const [k, v] of [["min_fee_minor", minFee], ["max_fee_minor", maxFee], ["min_amount_minor", minAmount], ["max_amount_minor", maxAmount]] as const) {
      if (v === undefined) clear.push(k);
      else (body as Record<string, unknown>)[k] = v;
    }
    if (f.endMode === "never") clear.push("effective_to");
    if (rule) return { r: await api.updateFee(bank.id, rule.id, { ...body, clear }), created: false };
    return { r: await api.createFee(bank.id, { ...body, code: f.code, event: f.event }), created: true };
  });

  const maintenance = f.event === "account_maintenance";
  const issuance = f.event === "card_issuance";
  const card = f.event === "card_payment";
  const [editCode, setEditCode] = useState(false);
  const [sample, setSample] = useState(maintenance ? "5,000" : "10,000");
  const before = cur.symbol ?? undefined;
  const after = cur.symbol ? undefined : cur.code;
  // A plain function, not a component, so inputs keep focus while typing.
  const money = (value: string, onChange: (v: string) => void, placeholder?: string, required?: boolean) => (
    <Affix before={before} after={after}>
      <Input value={value} onChange={(e) => onChange(e.target.value)} inputMode="decimal" placeholder={placeholder} required={required} />
    </Affix>
  );
  const preview = previewText(f, cur, sample);
  const startsAhead = f.startMode === "at" && gameNow && f.fromIso && new Date(f.fromIso) > new Date(gameNow);

  return (
    <Modal open size="wide" title={rule ? `Edit ${rule.name}` : "Add a fee"} onClose={onClose}
      description={<>Amounts in {cur.code} · game time in {tz}{gameNow && <> (now {formatGameTime(gameNow, tz)})</>}</>}
      onSubmit={async () => {
        const out = await save.run();
        if (out) onSaved(out.r, out.created);
      }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={save.pending}>{rule ? "Save changes" : "Add fee"}</Button></>}
    >
      <div className="form">
        <section className="form-section">
          <h3>Fee</h3>
          <div className="form-split">
            <div className="field">
              <Field label="Name" tip="Shown on the payer's statement next to the charge, e.g. “Player transfer fee”.">
                <Input value={f.name} required maxLength={80} placeholder="Player transfer fee" autoFocus
                  onChange={(e) => setF((s) => ({ ...s, name: e.target.value, code: s.codeTouched ? s.code : toCode(e.target.value) }))} />
              </Field>
              <div className="code-line">
                {editCode && !rule ? (
                  <Input className="input mono" aria-label="Code" value={f.code} required pattern="[a-z][a-z0-9_]{1,62}" style={{ maxWidth: 260, padding: "4px 8px" }}
                    onChange={(e) => setF((s) => ({ ...s, code: e.target.value, codeTouched: true }))} />
                ) : (
                  <>Code {f.code ? <span className="mono">{f.code}</span> : <em>fills in from the name</em>}{!rule && <button type="button" className="link-btn" onClick={() => setEditCode(true)}>Change</button>}</>
                )}
                <InfoTip label="About the code">The fee's stable ID in the API and in audit logs. {rule ? "It can't change." : "It can't change after you add the fee."}</InfoTip>
              </div>
            </div>
            <Field label="Charged on" tipAlign="end" tip={<>{EVENTS.find((x) => x.value === f.event)?.help}{rule ? " This can't change after the fee is added." : ""}</>}>
              <Select value={f.event} disabled={!!rule} onChange={(e) => {
                const ev = e.target.value as FeeEvent;
                setF((s) => ({ ...s, event: ev, model: ev === "account_maintenance" || ev === "card_issuance" ? "fixed" : s.model,
                  ...(ev === "card_issuance" ? { minAmount: "", maxAmount: "" } : {}) }));
              }}>
                {EVENTS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </Select>
            </Field>
          </div>
        </section>

        <section className="form-section">
          <h3>Charge</h3>
          <div className="field">
            <span>How it's worked out<InfoTip>Fixed: the same amount every time. Percentage: a share of the amount sent. Combined: both, e.g. ₦50 + 1%. Minimum and maximum then cap the result.</InfoTip></span>
            <Segmented<FeeModel> label="Fee model" value={f.model} onChange={(v) => set("model", v)}
              options={maintenance || issuance ? [{ value: "fixed", label: "Fixed amount" }] : [{ value: "fixed", label: "Fixed amount" }, { value: "percentage", label: "Percentage" }, { value: "combined", label: "Fixed + %" }]} />
          </div>
          <div className="form-row fill">
            {f.model !== "percentage" && <Field label="Fixed fee">{money(f.fixed, (v) => set("fixed", v), "50", true)}</Field>}
            {f.model !== "fixed" && (
              <Field label="Percentage" tip="Of the amount sent, up to 2 decimal places: 1.5 means 1.5%.">
                <Affix after="%"><Input value={f.percent} onChange={(e) => set("percent", e.target.value)} inputMode="decimal" placeholder="1" required /></Affix>
              </Field>
            )}
            {f.model !== "fixed" && (
              <>
                <Field label="Minimum" tip="Optional. The fee is never less than this.">{money(f.minFee, (v) => set("minFee", v), "None")}</Field>
                <Field label="Maximum" tipAlign="end" tip="Optional. The fee is never more than this.">{money(f.maxFee, (v) => set("maxFee", v), "None")}</Field>
              </>
            )}
          </div>
          {!issuance && <div className="preview" aria-live="polite">
            {preview === null ? <span className="muted">Fill in the charge to preview what a payment costs.</span> : (
              <>
                <span>Preview:</span>
                {maintenance ? <span>an account with a balance of</span> : card ? <span>a card payment of</span> : <span>a transfer of</span>}
                {money(sample, setSample)}
                <span>{preview}</span>
              </>
            )}
          </div>}
        </section>

        <section className="form-section">
          <h3>Who pays and who receives</h3>
          <div className="form-row">
            <div className="field">
              <span>Paid by<InfoTip>{maintenance ? "The account holder, from the account's balance. Accounts that can't cover it are skipped that month."
                : issuance || card ? "The cardholder, from the card's account. The merchant always gets the full payment."
                : "The sender, on top of the amount sent. The recipient always gets the full amount."}</InfoTip></span>
              <div className="static-value">{maintenance ? "The account holder" : issuance || card ? "The cardholder" : "The sender"}</div>
            </div>
            <Field label="Credited to" tipAlign="end" tip="Fee income is the bank's revenue. Levies payable holds money collected for someone else, such as a stamp-duty-style levy.">
              <Select value={f.kind} onChange={(e) => set("kind", e.target.value as FeeKind)}>
                <option value="fee">Bank fee income</option>
                <option value="levy">Levies payable</option>
              </Select>
            </Field>
          </div>
        </section>

        <section className="form-section">
          <h3>When it applies</h3>
          {!issuance && <div className="form-row">
            <Field label={maintenance ? "Balance at least" : "Amount at least"} tip={`Optional and inclusive: ${maintenance ? "accounts with this balance or more" : "transfers of this amount or more"} pay the fee.`}>
              {money(f.minAmount, (v) => set("minAmount", v), "No minimum")}
            </Field>
            <Field label={maintenance ? "Balance at most" : "Amount at most"} tipAlign="end" tip="Optional and inclusive: leave empty for no upper limit.">
              {money(f.maxAmount, (v) => set("maxAmount", v), "No maximum")}
            </Field>
          </div>}
          <div className="form-row schedule">
          <div className="field">
            <span>Starts<InfoTip>Times use the game clock ({tz}), not your computer's clock. Nothing is charged before the start.</InfoTip></span>
            <div className="inline-control">
              <Segmented<FormState["startMode"]> label="Starts" value={f.startMode} onChange={pickStart}
                options={[{ value: "now", label: rule && rule.state !== "scheduled" ? "Started" : "Immediately" }, { value: "at", label: "Later" }]} />
              {f.startMode === "at" && (
                <Input type="datetime-local" aria-label="Start game time" value={isoToZonedInput(f.fromIso, tz)}
                  onChange={(e) => set("fromIso", zonedInputToIso(e.target.value, tz) ?? "")} />
              )}
            </div>
            {f.startMode === "at" && gameNow && f.fromIso && (
              <small className="field-note">{startsAhead ? `Starts ${untilText(f.fromIso, gameNow)}; nothing is charged until then.` : "Already reached, so it charges straight away."}</small>
            )}
          </div>
          <div className="field">
            <span>Ends<InfoTip>The fee stops at this game time. Charges already made stay on statements.</InfoTip></span>
            <div className="inline-control">
              <Segmented<FormState["endMode"]> label="Ends" value={f.endMode} onChange={pickEnd}
                options={[{ value: "never", label: "Never" }, { value: "at", label: "At a time" }]} />
              {f.endMode === "at" && (
                <Input type="datetime-local" aria-label="End game time" value={isoToZonedInput(f.toIso, tz)}
                  onChange={(e) => set("toIso", zonedInputToIso(e.target.value, tz) ?? "")} />
              )}
            </div>
          </div>
          <div className="field">
            <span>Status<InfoTip align="end">A paused fee keeps its settings and history but is never charged. Resume it any time.</InfoTip></span>
            <div className="inline-control">
              <Segmented<FormState["status"]> label="Status" value={f.status} onChange={(v) => set("status", v)}
                options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Paused" }]} />
            </div>
          </div>
          </div>
        </section>

        {localError && <div className="notice error">{localError}</div>}
        <ErrorNotice error={save.error} />
      </div>
    </Modal>
  );
}

/**
 * What a rule charges on an amount, in minor units, exactly as the engine
 * computes it: fixed + amount × bps ÷ 10 000 rounded half up, then clamped to
 * [min, max]. Returns null when the amount is outside the rule's range.
 */
export function feeFor(amount: number, r: { model: FeeModel; fixed: number; bps: number; minFee: number | null; maxFee: number | null; minAmount: number | null; maxAmount: number | null }): number | null {
  if ((r.minAmount != null && amount < r.minAmount) || (r.maxAmount != null && amount > r.maxAmount)) return null;
  let fee = r.model === "percentage" ? 0n : BigInt(r.fixed);
  if (r.model !== "fixed") fee += (BigInt(amount) * BigInt(r.bps) + 5000n) / 10000n;
  if (r.minFee != null && fee < BigInt(r.minFee)) fee = BigInt(r.minFee);
  if (r.maxFee != null && fee > BigInt(r.maxFee)) fee = BigInt(r.maxFee);
  return Number(fee);
}

function previewText(f: FormState, cur: CurrencyLike, sample: string): string | null {
  const m = (v: string) => (v.trim() ? parseMoney(v, cur.exponent) : null);
  const fixed = f.model === "percentage" ? 0 : m(f.fixed);
  const bps = f.model === "fixed" ? 0 : f.percent.trim() ? parseMoney(f.percent, 2) : null;
  if (fixed === null || bps === null || (f.model !== "percentage" && fixed === 0)) return null;
  const amount = m(sample);
  if (amount === null) return "— enter an amount to preview.";
  const fee = feeFor(amount, { model: f.model, fixed, bps, minFee: f.model === "fixed" ? null : m(f.minFee), maxFee: f.model === "fixed" ? null : m(f.maxFee), minAmount: m(f.minAmount), maxAmount: m(f.maxAmount) });
  if (fee === null) return "→ outside the amount range, no fee";
  if (f.event === "account_maintenance") return `→ ${formatMoney(fee, cur)} a month`;
  return `→ fee ${formatMoney(fee, cur)}, total ${formatMoney(amount + fee, cur)}`;
}

function QuotePanel({ gameId, env, bank }: { gameId: string; env: GameTabProps["env"]; bank: Bank }) {
  const api = gameApi(useClient(), { gameId, env });
  const cur = bank.base_currency;
  const [amount, setAmount] = useState("");
  const [event, setEvent] = useState<FeeEvent>("transfer");
  const [quote, setQuote] = useState<FeeQuote | null>(null);
  const run = useAction(async () => {
    const minor = parseMoney(amount, cur.exponent);
    if (minor === null || minor <= 0) throw new Error(`Enter an amount in ${cur.code}`);
    return api.quoteFees({ bank_id: bank.id, event, amount_minor: minor });
  });
  return (
    <Panel title="Try a quote" sub="What a payment costs right now, exactly as the engine will charge it. Saved, active fees only.">
      <form className="form" onSubmit={async (e) => {
        e.preventDefault();
        const q = await run.run();
        if (q) setQuote(q);
      }}>
        <div className="form-row">
          <Field label="Charged on">
            <Select value={event} onChange={(e) => setEvent(e.target.value as FeeEvent)}>
              <option value="transfer">Player-to-player transfer</option>
              <option value="hold_capture">Hold capture (purchase)</option>
              <option value="card_payment">Card payment</option>
            </Select>
          </Field>
          <Field label={`Amount (${cur.code})`}><Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="20,000" required /></Field>
        </div>
        <div><Button type="submit" busy={run.pending}>Get quote</Button></div>
        <ErrorNotice error={run.error} />
      </form>
      {quote && (
        <dl className="facts" style={{ marginTop: 16 }}>
          <dt>Amount</dt><dd>{formatMoney(quote.amount_minor, cur)}</dd>
          {quote.fees.length === 0 && <><dt>Fees</dt><dd>None</dd></>}
          {quote.fees.map((c) => <FactLine key={c.fee_rule_id} label={c.name + (c.kind === "levy" ? " (levy)" : "")} value={formatMoney(c.amount_minor, cur)} />)}
          <dt><strong>Sender pays</strong></dt><dd><strong>{formatMoney(quote.total_debit_minor, cur)}</strong></dd>
        </dl>
      )}
    </Panel>
  );
}

function FactLine({ label, value }: { label: string; value: string }) {
  return <><dt>{label}</dt><dd>{value}</dd></>;
}

function BooksPanel({ gameId, env, bank }: { gameId: string; env: GameTabProps["env"]; bank: Bank }) {
  const api = gameApi(useClient(), { gameId, env });
  const books = useQuery(`dev:books:${gameId}:${env}:${bank.id}`, () => api.books(bank.id), { refreshMs: 15_000 });
  const cur = bank.base_currency;
  return (
    <Panel title="Collected so far" sub="From the bank's own ledger accounts. Refunded fees are already netted off.">
      <ErrorNotice error={books.error} onRetry={books.reload} />
      {books.loading && !books.data && <Loading />}
      {books.data && (
        <dl className="facts">
          <dt>Fee income (the bank's revenue)</dt><dd>{formatMoney(books.data.fee_income_minor, cur)}</dd>
          <dt>Levies payable (held for others)</dt><dd>{formatMoney(books.data.levies_payable_minor, cur)}</dd>
        </dl>
      )}
    </Panel>
  );
}
